import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Bike, MapPin, Flag, Package, User, Lock, ArrowRight, Check, MapPinned } from "lucide-react";
import { rpc, supabase } from "@/lib/supabase";
import { get_road_distance } from "@/lib/routing";
import { PageHeader, Card } from "@/components/kit";
import { MapPicker } from "@/components/MapPicker";
import { GoogleMapPicker } from "@/components/GoogleMapPicker";
import { AddressSearch } from "@/components/AddressSearch";
import { formatCOP, formatKm, VEHICLE_LABELS, CARGO_CATEGORIES } from "@/lib/format";

const GOOGLE_MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const field =
  "w-full rounded-xl border border-[#27272A] bg-[#0d0d0f] px-3.5 py-2.5 text-sm text-white placeholder-gray-500 outline-none focus:border-[#FF6A00]";
const labelCls = "mb-1.5 block text-xs font-bold uppercase tracking-wider text-gray-400";

export default function CreateService() {
  const nav = useNavigate();
  const [mode, setMode] = useState("pickup");
  const [pickup, setPickup] = useState({});
  const [dropoff, setDropoff] = useState({});
  const [quote, setQuote] = useState(null);
  const [quoting, setQuoting] = useState(false);
  const [saving, setSaving] = useState(false);
  // Mapa de Google como principal; Leaflet queda como respaldo si falta la clave o falla la carga
  const [useGoogleMap, setUseGoogleMap] = useState(!!GOOGLE_MAPS_KEY);
  const [f, setF] = useState({
    pickup_address: "", pickup_city: "", pickup_reference: "",
    dropoff_address: "", dropoff_city: "", dropoff_reference: "",
    recipient_name: "", recipient_phone: "",
    package_category: "", package_count: "1", package_weight_kg: "",
    length_cm: "", width_cm: "", height_cm: "", package_notes: "",
  });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  // Límites de capacidad centralizados y configurables por ADMIN (pricing_rules)
  const [limits, setLimits] = useState(null);
  useEffect(() => {
    supabase
      .from("pricing_rules")
      .select("max_packages,max_weight_kg,max_length_cm,max_width_cm,max_height_cm,heavy_weight_kg")
      .eq("vehicle_type", "MOTORCYCLE")
      .maybeSingle()
      .then(({ data }) => setLimits(data || null));
  }, []);

  // Pin fijado/ajustado manualmente en el mapa que aún no se ha confirmado
  const [pendingPin, setPendingPin] = useState(null); // "pickup" | "dropoff" | null

  const onPick = useCallback((which, point) => {
    if (which === "pickup") {
      setPickup(point);
    } else {
      setDropoff(point);
    }
    setPendingPin(which); // ubicación manual requiere confirmación explícita
  }, []);

  // Cotización y ruta SOLO con coordenadas confirmadas (no con un pin sin confirmar)
  const bothSet = pickup.lat != null && dropoff.lat != null && pendingPin == null;
  const [attempted, setAttempted] = useState(false);
  const [route, setRoute] = useState(null);
  const [routeError, setRouteError] = useState(null);
  const [quoteNonce, setQuoteNonce] = useState(0);

  useEffect(() => {
    if (!bothSet) {
      setQuote(null);
      setRoute(null);
      setRouteError(null);
      return;
    }
    let active = true;
    setQuoting(true);
    setQuote(null);
    setRouteError(null);
    // Debounce: espera a que el usuario termine de mover el mapa antes de pedir la ruta por carretera
    const timer = setTimeout(async () => {
      const r = await get_road_distance(pickup, dropoff);
      if (!active) return;
      if (!r.ok) {
        setRouteError(r.error);
        setRoute(null);
        setQuoting(false);
        return;
      }
      setRoute(r);
      try {
        const q = await rpc("quote_preview", { p_vehicle_type: "MOTORCYCLE", p_distance_km: r.distanceKm });
        if (active) setQuote(q);
      } catch {
        if (active) setRouteError("No se pudo calcular la cotización. Intenta de nuevo.");
      }
      if (active) setQuoting(false);
    }, 700);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [pickup, dropoff, bothSet, quoteNonce]);

  const missing = [];
  if (pickup.lat == null) missing.push("Selecciona la dirección de ORIGEN en el buscador (o fija el punto en el mapa)");
  if (dropoff.lat == null) missing.push("Selecciona la dirección de DESTINO en el buscador (o fija el punto en el mapa)");
  if (pendingPin === "pickup") missing.push("Confirma la ubicación de ORIGEN en el mapa (botón «Confirmar ubicación»)");
  if (pendingPin === "dropoff") missing.push("Confirma la ubicación de DESTINO en el mapa (botón «Confirmar ubicación»)");
  if (!f.pickup_address.trim()) missing.push("Escribe la dirección de origen");
  if (!f.dropoff_address.trim()) missing.push("Escribe la dirección de destino");
  // Carga: obligatoria, positiva y dentro de límites configurados
  const count = Number(f.package_count);
  const weight = Number(f.package_weight_kg);
  const len = Number(f.length_cm), wid = Number(f.width_cm), hei = Number(f.height_cm);
  if (!f.package_category) missing.push("Selecciona la categoría de carga");
  if (!f.package_count || !Number.isInteger(count) || count < 1) missing.push("La cantidad de paquetes debe ser un entero ≥ 1");
  if (!f.package_weight_kg || weight <= 0) missing.push("Ingresa un peso total válido mayor a 0 kg (incluye el empaque)");
  if (!f.length_cm || len <= 0 || !f.width_cm || wid <= 0 || !f.height_cm || hei <= 0)
    missing.push("Ingresa dimensiones válidas: largo, ancho y alto mayores a 0 cm");
  if (limits) {
    if (count > limits.max_packages) missing.push(`Máximo ${limits.max_packages} paquete(s) por envío`);
    if (weight > Number(limits.max_weight_kg)) missing.push(`El peso máximo permitido es ${limits.max_weight_kg} kg (incluye empaque)`);
    if (len > Number(limits.max_length_cm) || wid > Number(limits.max_width_cm) || hei > Number(limits.max_height_cm))
      missing.push(`Dimensiones máximas: ${limits.max_length_cm}×${limits.max_width_cm}×${limits.max_height_cm} cm`);
  }
  if (bothSet && quoting) missing.push("Espera a que termine de calcularse la ruta y la cotización");
  if (bothSet && !quoting && routeError) missing.push("Ruta por carretera: " + routeError);
  if (bothSet && !quoting && !routeError && !quote) missing.push("No se pudo calcular la cotización, reintenta");

  const submit = async (e) => {
    e.preventDefault();
    setAttempted(true);
    if (missing.length > 0) {
      toast.error(missing[0]);
      return;
    }
    setSaving(true);
    try {
      const sid = await rpc("create_service", {
        p_vehicle_type: "MOTORCYCLE",
        p_pickup_address: f.pickup_address, p_pickup_city: f.pickup_city, p_pickup_reference: f.pickup_reference,
        p_pickup_lat: pickup.lat, p_pickup_lng: pickup.lng,
        p_dropoff_address: f.dropoff_address, p_dropoff_city: f.dropoff_city, p_dropoff_reference: f.dropoff_reference,
        p_dropoff_lat: dropoff.lat, p_dropoff_lng: dropoff.lng,
        p_recipient_name: f.recipient_name, p_recipient_phone: f.recipient_phone,
        p_package_category: f.package_category,
        p_package_count: count,
        p_package_weight_kg: weight,
        p_package_length_cm: len, p_package_width_cm: wid, p_package_height_cm: hei,
        p_package_notes: f.package_notes,
        p_distance_km: route.distanceKm,
        p_duration_min: route.durationMin,
      });
      toast.success("Envío creado. Buscando conductor…");
      nav(`/business/service/${sid}`);
    } catch (err) {
      toast.error(err?.message || "No se pudo crear el envío");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit}>
      <PageHeader title="Solicitar envío" subtitle="Fija origen y destino, describe la carga y confirma." testId="create-service" />

      <div className="grid gap-6 lg:grid-cols-5">
        <div className="space-y-6 lg:col-span-3">
          {/* Map */}
          <Card>
            {/* Búsqueda de direcciones (flujo principal) */}
            <div className="mb-3 grid gap-3 sm:grid-cols-2">
              <AddressSearch
                icon={MapPin}
                label="Punto de recogida"
                placeholder="Busca dirección, calle, barrio o lugar..."
                accent="#FF6A00"
                testId="pickup-address-search"
                onSelect={(p) => {
                  if (p.manual) {
                    setMode("pickup");
                    setPendingPin("pickup");
                    setF((prev) => ({ ...prev, ...(p.typed ? { pickup_address: p.typed } : {}) }));
                    toast.message("Toca el mapa para fijar el punto exacto y confírmalo");
                    return;
                  }
                  setPickup({ lat: p.lat, lng: p.lng });
                  // conserva el texto completo escrito por el usuario (nunca el nombre genérico de la calle)
                  setF((prev) => ({ ...prev, pickup_address: p.typed || p.label, ...(p.city ? { pickup_city: p.city } : {}) }));
                  // Aproximada (solo calle/zona): exige confirmar el pin exacto
                  if (p.kind && p.kind !== "house") {
                    setPendingPin("pickup");
                    setMode("pickup");
                    toast.message("Ubicación aproximada: ajusta el pin exacto de recogida y confírmalo");
                  } else {
                    setPendingPin(null);
                    setMode("dropoff");
                  }
                }}
              />
              <AddressSearch
                icon={Flag}
                label="Punto de destino"
                placeholder="Busca dirección, calle, barrio o lugar..."
                accent="#34D399"
                testId="dropoff-address-search"
                onSelect={(p) => {
                  if (p.manual) {
                    setMode("dropoff");
                    setPendingPin("dropoff");
                    setF((prev) => ({ ...prev, ...(p.typed ? { dropoff_address: p.typed } : {}) }));
                    toast.message("Toca el mapa para fijar el punto exacto y confírmalo");
                    return;
                  }
                  setDropoff({ lat: p.lat, lng: p.lng });
                  setF((prev) => ({ ...prev, dropoff_address: p.typed || p.label, ...(p.city ? { dropoff_city: p.city } : {}) }));
                  if (p.kind && p.kind !== "house") {
                    setPendingPin("dropoff");
                    setMode("dropoff");
                    toast.message("Ubicación aproximada: ajusta el pin exacto de destino y confírmalo");
                  } else {
                    setPendingPin(null);
                  }
                }}
              />
            </div>

            <div className="mb-3 flex items-center gap-2">
              <button
                type="button"
                onClick={() => setMode("pickup")}
                data-testid="map-mode-pickup"
                className={`flex-1 rounded-xl border px-3 py-2 text-sm font-bold transition-all ${
                  mode === "pickup" ? "border-[#FF6A00] bg-[#FF6A00]/10 text-[#FF6A00]" : "border-[#27272A] text-gray-400"
                }`}
              >
                <MapPin size={15} className="mr-1 inline" /> Origen {pickup.lat != null && "✓"}
              </button>
              <button
                type="button"
                onClick={() => setMode("dropoff")}
                data-testid="map-mode-dropoff"
                className={`flex-1 rounded-xl border px-3 py-2 text-sm font-bold transition-all ${
                  mode === "dropoff" ? "border-emerald-400 bg-emerald-400/10 text-emerald-400" : "border-[#27272A] text-gray-400"
                }`}
              >
                <Flag size={15} className="mr-1 inline" /> Destino {dropoff.lat != null && "✓"}
              </button>
            </div>
            {pendingPin && (
              <div className="mb-3 flex items-center justify-between gap-2 rounded-xl border border-[#FF6A00]/40 bg-[#FF6A00]/10 px-3 py-2.5" data-testid="pending-pin-alert">
                <div className="flex items-center gap-1.5 text-xs text-gray-300">
                  <MapPinned size={14} className="text-[#FF6A00]" />
                  {(pendingPin === "pickup" ? pickup.lat : dropoff.lat) != null
                    ? `Pin de ${pendingPin === "pickup" ? "origen (A)" : "destino (B)"} fijado — arrástralo si necesitas ajustar y confirma.`
                    : `Toca el mapa para fijar el pin de ${pendingPin === "pickup" ? "origen (A)" : "destino (B)"}.`}
                </div>
                {(pendingPin === "pickup" ? pickup.lat : dropoff.lat) != null && (
                  <button
                    type="button"
                    data-testid={`confirm-pin-${pendingPin}`}
                    onClick={() => {
                      setPendingPin(null);
                      toast.success("Ubicación confirmada");
                      if (pendingPin === "pickup" && dropoff.lat == null) setMode("dropoff");
                    }}
                    className="flex shrink-0 items-center gap-1 rounded-lg bg-[#FF6A00] px-3 py-1.5 text-xs font-extrabold text-black transition-transform active:scale-95"
                  >
                    <Check size={13} /> Confirmar ubicación
                  </button>
                )}
              </div>
            )}
            {useGoogleMap ? (
              <GoogleMapPicker
                apiKey={GOOGLE_MAPS_KEY}
                pickup={pickup}
                dropoff={dropoff}
                mode={mode}
                route={route?.geometry}
                onPick={onPick}
                onMarkerMove={(which, point) => {
                  if (which === "pickup") setPickup(point);
                  else setDropoff(point);
                  setPendingPin(which);
                }}
                onFailed={() => setUseGoogleMap(false)}
              />
            ) : (
              <MapPicker
                pickup={pickup}
                dropoff={dropoff}
                mode={mode}
                route={route?.geometry}
                onPick={onPick}
                onMarkerMove={(which, point) => {
                  if (which === "pickup") setPickup(point);
                  else setDropoff(point);
                  setPendingPin(which);
                }}
              />
            )}
          </Card>

          {/* Addresses */}
          <Card>
            <h3 className="mb-4 font-heading text-base font-bold text-white">Direcciones</h3>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className={labelCls}>Dirección de origen</label>
                <input className={field} value={f.pickup_address} onChange={set("pickup_address")} placeholder="Calle 44 #70-20" data-testid="create-service-pickup-input" />
              </div>
              <div>
                <label className={labelCls}>Ciudad origen</label>
                <input className={field} value={f.pickup_city} onChange={set("pickup_city")} placeholder="Ej: El Cerrito" data-testid="create-service-pickup-city" />
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Referencia origen</label>
                <input className={field} value={f.pickup_reference} onChange={set("pickup_reference")} placeholder="Portón negro, timbre 2" />
              </div>
              <div>
                <label className={labelCls}>Dirección de destino</label>
                <input className={field} value={f.dropoff_address} onChange={set("dropoff_address")} placeholder="Carrera 80 #45-10" data-testid="create-service-dropoff-input" />
              </div>
              <div>
                <label className={labelCls}>Ciudad destino</label>
                <input className={field} value={f.dropoff_city} onChange={set("dropoff_city")} placeholder="Ej: Ginebra" data-testid="create-service-dropoff-city" />
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Referencia destino</label>
                <input className={field} value={f.dropoff_reference} onChange={set("dropoff_reference")} placeholder="Local 3, segundo piso" />
              </div>
            </div>
          </Card>

          {/* Package + recipient */}
          <Card>
            <h3 className="mb-4 flex items-center gap-2 font-heading text-base font-bold text-white">
              <Package size={17} className="text-[#FF6A00]" /> Carga y destinatario
            </h3>
            {limits && (
              <p className="mb-4 rounded-xl border border-[#27272A] bg-[#0d0d0f] px-3 py-2 text-[11px] leading-relaxed text-gray-400" data-testid="cargo-limits-note">
                Límites por envío (moto): máx. <b className="text-gray-200">{limits.max_packages}</b> paquete(s),{" "}
                <b className="text-gray-200">{limits.max_weight_kg} kg</b> (incluye empaque),{" "}
                <b className="text-gray-200">{limits.max_length_cm}×{limits.max_width_cm}×{limits.max_height_cm} cm</b> por paquete.
              </p>
            )}
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className={labelCls}>Categoría de carga</label>
                <select className={field} value={f.package_category} onChange={set("package_category")} data-testid="create-service-category">
                  <option value="">Selecciona…</option>
                  {CARGO_CATEGORIES.map((c) => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelCls}>Cantidad de paquetes</label>
                <input type="number" step="1" min="1" className={field} value={f.package_count} onChange={set("package_count")} placeholder="1" data-testid="create-service-count" />
              </div>
              <div>
                <label className={labelCls}>Peso total (kg, incl. empaque)</label>
                <input type="number" step="0.1" min="0" className={field} value={f.package_weight_kg} onChange={set("package_weight_kg")} placeholder="3.5" data-testid="create-service-weight" />
              </div>
              <div>
                <label className={labelCls}>Dimensiones (cm)</label>
                <div className="grid grid-cols-3 gap-2">
                  <input type="number" step="0.1" min="0" className={field} value={f.length_cm} onChange={set("length_cm")} placeholder="Largo" data-testid="create-service-length" />
                  <input type="number" step="0.1" min="0" className={field} value={f.width_cm} onChange={set("width_cm")} placeholder="Ancho" data-testid="create-service-width" />
                  <input type="number" step="0.1" min="0" className={field} value={f.height_cm} onChange={set("height_cm")} placeholder="Alto" data-testid="create-service-height" />
                </div>
              </div>
              <div>
                <label className={labelCls}>Nombre del destinatario</label>
                <input className={field} value={f.recipient_name} onChange={set("recipient_name")} placeholder="Quién recibe" />
              </div>
              <div>
                <label className={labelCls}>Teléfono destinatario</label>
                <input className={field} value={f.recipient_phone} onChange={set("recipient_phone")} placeholder="300 000 0000" />
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Observaciones</label>
                <textarea className={field} rows={2} value={f.package_notes} onChange={set("package_notes")} placeholder="Frágil, manipular con cuidado…" />
              </div>
            </div>
          </Card>
        </div>

        {/* Summary */}
        <div className="lg:col-span-2">
          <div className="lg:sticky lg:top-24">
            <Card>
              <h3 className="mb-4 font-heading text-base font-bold text-white">Cotización</h3>

              <div className="mb-4 flex items-center justify-between rounded-xl border border-[#FF6A00]/40 bg-[#FF6A00]/10 px-3 py-2.5">
                <div className="flex items-center gap-2 text-sm font-semibold text-white">
                  <Bike size={16} className="text-[#FF6A00]" /> {VEHICLE_LABELS.MOTORCYCLE}
                </div>
                <span className="text-[11px] font-bold uppercase text-[#FF6A00]">Activo</span>
              </div>
              <p className="mb-4 flex items-center gap-1.5 text-[11px] text-gray-500">
                <Lock size={11} /> Otros vehículos (carro, camioneta, van, camión) próximamente.
              </p>

              {!bothSet ? (
                <div className="rounded-xl border border-dashed border-[#27272A] px-4 py-8 text-center text-sm text-gray-500" data-testid="quote-empty">
                  Fija origen y destino en el mapa para ver la cotización.
                </div>
              ) : quoting ? (
                <div className="py-8 text-center text-sm text-gray-400" data-testid="quote-loading">Calculando ruta por carretera y cotización…</div>
              ) : routeError ? (
                <div className="rounded-xl border border-red-500/40 bg-red-500/10 px-4 py-4" data-testid="route-error">
                  <div className="text-sm font-semibold text-red-400">{routeError}</div>
                  <p className="mt-1 text-xs text-gray-400">No se generó ninguna cotización con valores incorrectos.</p>
                  <button
                    type="button"
                    onClick={() => setQuoteNonce((n) => n + 1)}
                    data-testid="route-retry-button"
                    className="mt-3 rounded-lg bg-[#18181B] px-4 py-2 text-sm font-bold text-white transition-colors hover:bg-[#27272A]"
                  >
                    Reintentar
                  </button>
                </div>
              ) : quote ? (
                <div data-testid="quote-summary">
                  <div className="mb-1 flex items-center justify-between">
                    <span className="text-sm text-gray-400">Distancia</span>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-white">{formatKm(quote.distance_km)}</span>
                      <span
                        className="inline-flex items-center gap-1 rounded-full border border-emerald-500 bg-emerald-500/15 px-3 py-1 text-xs font-extrabold uppercase tracking-wider text-emerald-400"
                        data-testid="road-distance-badge"
                      >
                        Por carretera
                      </span>
                    </div>
                  </div>
                  {route?.durationMin != null && (
                    <div className="mb-3 flex items-center justify-between text-sm" data-testid="route-duration">
                      <span className="text-gray-400">Tiempo estimado</span>
                      <span className="font-semibold text-white">~{route.durationMin} min</span>
                    </div>
                  )}
                  <div className="rounded-xl bg-[#0d0d0f] p-4">
                    <div className="flex items-baseline justify-between">
                      <span className="text-sm text-gray-400">Total a pagar</span>
                      <span className="font-heading text-3xl font-black text-[#FF6A00]" data-testid="quote-price">
                        {formatCOP(quote.price)}
                      </span>
                    </div>
                    <div className="mt-3 space-y-1.5 border-t border-[#1e1e21] pt-3 text-xs">
                      <div className="flex justify-between text-gray-500">
                        <span>Conductor (75%)</span>
                        <span className="text-gray-300" data-testid="quote-driver-earning">{formatCOP(quote.driver_earning)}</span>
                      </div>
                      <div className="flex justify-between text-gray-500">
                        <span data-testid="quote-platform-label">RAPIFAST (25%)</span>
                        <span className="text-gray-300" data-testid="quote-platform-commission">{formatCOP(quote.platform_commission)}</span>
                      </div>
                    </div>
                  </div>
                  <p className="mt-3 text-[11px] leading-relaxed text-gray-600">
                    Distancia y tiempo calculados por <b className="text-gray-400">ruta de carretera</b> (
                    {route?.provider === "google" ? "Google Routes" : "OSRM, respaldo temporal"}).
                  </p>
                </div>
              ) : null}

              {attempted && missing.length > 0 && (
                <div className="mt-4 rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3" data-testid="missing-fields-alert">
                  <div className="mb-1.5 text-xs font-bold uppercase tracking-wider text-amber-400">
                    Para confirmar te falta:
                  </div>
                  <ul className="list-inside list-disc space-y-1 text-sm text-amber-200">
                    {missing.map((m) => (
                      <li key={m}>{m}</li>
                    ))}
                  </ul>
                </div>
              )}

              <button
                type="submit"
                disabled={saving}
                data-testid="create-service-submit"
                className="mt-5 flex w-full items-center justify-center gap-2 rounded-xl bg-[#FF6A00] py-3.5 font-extrabold text-black shadow-md shadow-[#FF6A00]/25 transition-transform active:scale-95 disabled:opacity-50"
              >
                {saving ? "Creando…" : "Confirmar y buscar conductor"} <ArrowRight size={18} />
              </button>
            </Card>
          </div>
        </div>
      </div>
    </form>
  );
}
